import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'qrcode';
import { api } from '../api';

const CARD_W = 340;  // ~85.6mm at 96dpi
const CARD_H = 216;  // ~54mm  at 96dpi
const BRAND  = '#1b3a6b';
const ACCENT = '#e8f0fb';

const AVATAR_COLORS = [
  '#1b3a6b','#2563eb','#7c3aed','#db2777',
  '#d97706','#16a34a','#0891b2','#dc2626',
];

function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initials(s) {
  return [(s.firstName || '')[0], (s.lastName || '')[0]]
    .filter(Boolean).join('').toUpperCase() || '??';
}

function className(cs) {
  if (!cs) return '—';
  return cs.name ? `${cs.name}` : '—';
}

// ── ID Card visual (rendered inside iframe for printing) ─────────────────────
function CardFace({ student, schoolName, qrDataUrl }) {
  const bg       = BRAND;
  const initCol  = avatarColor((student.firstName || '') + (student.lastName || ''));
  const fullName = `${student.firstName || ''} ${student.lastName || ''}`.trim();
  const year     = new Date().getFullYear();

  return (
    <div style={{
      width: CARD_W, height: CARD_H,
      background: '#fff',
      borderRadius: 10,
      overflow: 'hidden',
      boxShadow: '0 4px 24px rgba(0,0,0,.18)',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
    }}>
      {/* Top bar */}
      <div style={{ background: bg, padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 900, fontSize: 13, color: bg, flexShrink: 0,
        }}>S</div>
        <div style={{ color: '#fff', fontWeight: 700, fontSize: 12, letterSpacing: .3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {schoolName || 'School Management System'}
        </div>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', padding: '10px 12px', gap: 10, height: CARD_H - 58 }}>

        {/* Left: photo/avatar + QR */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {student.photo ? (
            <img src={student.photo} alt={fullName} style={{
              width: 56, height: 56, borderRadius: 8, objectFit: 'cover',
              border: `2px solid ${bg}`,
            }} />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: 8,
              background: initCol, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 20,
              border: `2px solid ${bg}`,
            }}>{initials(student)}</div>
          )}

          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR" style={{ width: 72, height: 72, borderRadius: 4, border: '1.5px solid #e0e6f0' }} />
          ) : (
            <div style={{ width: 72, height: 72, background: '#f0f4f8', borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#aaa' }}>
              Loading QR…
            </div>
          )}
        </div>

        {/* Right: student details */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: bg, marginBottom: 1, lineHeight: 1.2 }}>
              {fullName}
            </div>
            <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>STUDENT IDENTITY CARD</div>

            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 10 }}>
              <tbody>
                {[
                  ['Adm No',  student.admissionNo || '—'],
                  ['Class',   className(student.classSection)],
                  ['DOB',     student.dob ? student.dob.slice(0, 10) : '—'],
                  ['Blood',   student.bloodGroup || '—'],
                  ['Contact', student.phone || '—'],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ color: '#888', paddingRight: 6, paddingBottom: 2, whiteSpace: 'nowrap' }}>{label}</td>
                    <td style={{ fontWeight: 600, color: '#1a1a2e', paddingBottom: 2 }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: bg, padding: '4px 12px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ color: 'rgba(255,255,255,.7)', fontSize: 9 }}>
          Valid: Academic Year {year}–{year + 1}
        </span>
        <span style={{ color: 'rgba(255,255,255,.5)', fontSize: 8, letterSpacing: .5 }}>
          SMABIZZ.COM
        </span>
      </div>
    </div>
  );
}

// ── Print helper: writes card HTML into an iframe and prints ─────────────────
function printCard(student, schoolName, qrDataUrl) {
  const fullName = `${student.firstName || ''} ${student.lastName || ''}`.trim();
  const year     = new Date().getFullYear();
  const initCol  = avatarColor((student.firstName || '') + (student.lastName || ''));
  const init     = initials(student);

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <title>ID Card — ${fullName}</title>
  <style>
    @page { size: 85.6mm 54mm; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { width: 85.6mm; height: 54mm; font-family: Arial, sans-serif; }
    .card { width: 85.6mm; height: 54mm; background: #fff; overflow: hidden; position: relative; }
    .top  { background: ${BRAND}; padding: 5px 8px; display: flex; align-items: center; gap: 6px; }
    .logo { width: 20px; height: 20px; border-radius: 50%; background: #fff; display: flex; align-items: center;
            justify-content: center; font-weight: 900; font-size: 10px; color: ${BRAND}; flex-shrink: 0; }
    .sch-name { color: #fff; font-weight: 700; font-size: 9px; letter-spacing: .3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .body { display: flex; padding: 6px 8px; gap: 7px; }
    .avatar { width: 40px; height: 40px; border-radius: 6px; background: ${initCol};
              display: flex; align-items: center; justify-content: center;
              color: #fff; font-weight: 800; font-size: 15px; border: 1.5px solid ${BRAND}; flex-shrink: 0; }
    .qr-img { width: 52px; height: 52px; display: block; }
    .left   { display: flex; flex-direction: column; align-items: center; gap: 4px; flex-shrink: 0; }
    .right  { flex: 1; }
    .sname  { font-weight: 800; font-size: 10.5px; color: ${BRAND}; line-height: 1.2; margin-bottom: 1px; }
    .stitle { font-size: 7.5px; color: #888; margin-bottom: 4px; }
    table   { border-collapse: collapse; width: 100%; font-size: 7.5px; }
    td      { padding-bottom: 1.5px; }
    td:first-child { color: #888; padding-right: 5px; white-space: nowrap; }
    td:last-child  { font-weight: 600; color: #1a1a2e; }
    .bot { position: absolute; bottom: 0; left: 0; right: 0; background: ${BRAND}; padding: 2.5px 8px;
           display: flex; justify-content: space-between; align-items: center; }
    .bot span { color: rgba(255,255,255,.7); font-size: 6.5px; }
    .bot small { color: rgba(255,255,255,.4); font-size: 6px; letter-spacing: .5px; }
  </style>
  </head><body><div class="card">
    <div class="top">
      <div class="logo">S</div>
      <div class="sch-name">${schoolName || 'School'}</div>
    </div>
    <div class="body">
      <div class="left">
        ${student.photo
          ? `<img class="avatar" src="${student.photo}" style="object-fit:cover;" alt="photo">`
          : `<div class="avatar">${init}</div>`}
        ${qrDataUrl ? `<img class="qr-img" src="${qrDataUrl}" alt="QR">` : ''}
      </div>
      <div class="right">
        <div class="sname">${fullName}</div>
        <div class="stitle">STUDENT IDENTITY CARD</div>
        <table><tbody>
          <tr><td>Adm No</td><td>${student.admissionNo || '—'}</td></tr>
          <tr><td>Class</td><td>${className(student.classSection)}</td></tr>
          <tr><td>DOB</td><td>${student.dob ? student.dob.slice(0, 10) : '—'}</td></tr>
          <tr><td>Blood</td><td>${student.bloodGroup || '—'}</td></tr>
          <tr><td>Contact</td><td>${student.phone || '—'}</td></tr>
        </tbody></table>
      </div>
    </div>
    <div class="bot">
      <span>Valid: Academic Year ${year}–${year + 1}</span>
      <small>SMABIZZ.COM</small>
    </div>
  </div></body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:0';
  document.body.appendChild(iframe);
  iframe.contentDocument.open();
  iframe.contentDocument.write(html);
  iframe.contentDocument.close();
  iframe.contentWindow.onload = () => {
    iframe.contentWindow.print();
    setTimeout(() => document.body.removeChild(iframe), 2000);
  };
}

// ── Main modal component ─────────────────────────────────────────────────────
export default function StudentIdCard({ student, schoolName, onClose }) {
  const [qrDataUrl, setQrDataUrl]   = useState(null);
  const [token, setToken]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [err, setErr]               = useState('');
  const [photo, setPhoto]           = useState(student.photo || null);
  const [photoSaving, setPhotoSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.getIdCardToken(student.id)
      .then(res => {
        if (res.error) { setErr(res.error); setLoading(false); return; }
        setToken(res.token);
        return QRCode.toDataURL(res.token, {
          width: 200, margin: 1,
          color: { dark: '#1b3a6b', light: '#ffffff' },
          errorCorrectionLevel: 'H',
        });
      })
      .then(url => { if (url) setQrDataUrl(url); setLoading(false); })
      .catch(e => { setErr(e.message || 'Failed to generate ID card'); setLoading(false); });
  }, [student.id]);

  const handlePhotoUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target.result;
      setPhoto(dataUrl);
      setPhotoSaving(true);
      try {
        await api.uploadStudentPhoto(student.id, dataUrl);
      } catch (_) {}
      setPhotoSaving(false);
    };
    reader.readAsDataURL(file);
  }, [student.id]);

  const studentWithPhoto = { ...student, photo };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '100%', maxWidth: 420,
        boxShadow: '0 12px 48px rgba(0,0,0,.28)',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: BRAND }}>🪪 Student ID Card</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20,
            cursor: 'pointer', color: '#9aa0a6', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Card preview */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          {loading ? (
            <div style={{ padding: 40, color: '#9aa0a6', fontSize: 14 }}>Generating secure QR…</div>
          ) : err ? (
            <div style={{ color: '#c5221f', fontSize: 13, background: '#fce8e6',
              borderRadius: 8, padding: '10px 14px', width: '100%' }}>{err}</div>
          ) : (
            <>
              <CardFace student={studentWithPhoto} schoolName={schoolName} qrDataUrl={qrDataUrl} />

              {/* Photo upload */}
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', borderRadius: 8, border: '1px solid #dadce0',
                background: '#f8fafc', fontSize: 13, fontWeight: 600,
                color: '#5f6368', cursor: 'pointer',
              }}>
                {photoSaving ? '⏳ Saving…' : photo ? '📷 Change Photo' : '📷 Upload Photo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} />
              </label>
            </>
          )}
        </div>

        {/* Actions */}
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8,
            border: '1px solid #dadce0', background: '#fff', color: '#5f6368',
            fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Close
          </button>
          <button
            disabled={!qrDataUrl || loading}
            onClick={() => printCard(studentWithPhoto, schoolName, qrDataUrl)}
            style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
              background: qrDataUrl ? BRAND : '#9aa0a6', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: qrDataUrl ? 'pointer' : 'not-allowed' }}>
            🖨️ Print ID Card
          </button>
        </div>
      </div>
    </div>
  );
}

// ── QR Scanner component (for attendance) ────────────────────────────────────
export function QrAttendanceScanner({ onClose }) {
  const [input, setInput]   = useState('');
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus so a physical USB QR scanner can type directly into this field
    if (inputRef.current) inputRef.current.focus();
  }, []);

  async function handleScan(token) {
    const t = (token || input).trim();
    if (!t) return;
    setScanning(true);
    setResult(null);
    try {
      const res = await api.scanIdCardAttendance(t);
      setResult(res.valid === false
        ? { ok: false, msg: res.error || 'Invalid QR code' }
        : { ok: true,  msg: `Present ✓  ${res.firstName} ${res.lastName} (${res.admissionNo})` });
    } catch (e) {
      setResult({ ok: false, msg: e.message || 'Scan failed' });
    } finally {
      setScanning(false);
      setInput('');
      if (inputRef.current) inputRef.current.focus();
    }
  }

  // Physical QR scanners emit a newline after the payload
  function handleKeyDown(e) {
    if (e.key === 'Enter') handleScan();
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 380,
        boxShadow: '0 12px 48px rgba(0,0,0,.28)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: BRAND }}>QR Attendance Scanner</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            fontSize: 20, cursor: 'pointer', color: '#9aa0a6' }}>&times;</button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 12, lineHeight: 1.5 }}>
            Point your QR scanner at the student's ID card, or paste the token below.
            Attendance will be marked <strong>Present</strong> for today automatically.
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Scan or paste QR token…"
              style={{ flex: 1, padding: '10px 12px', borderRadius: 8,
                border: '1.5px solid #dadce0', fontSize: 13, outline: 'none' }}
            />
            <button
              onClick={() => handleScan()}
              disabled={scanning || !input.trim()}
              style={{ padding: '10px 16px', borderRadius: 8, border: 'none',
                background: BRAND, color: '#fff', fontWeight: 700,
                cursor: scanning ? 'not-allowed' : 'pointer', fontSize: 13,
                opacity: scanning || !input.trim() ? .6 : 1 }}>
              {scanning ? '…' : 'Mark'}
            </button>
          </div>

          {result && (
            <div style={{
              padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: result.ok ? '#e6f4ea' : '#fce8e6',
              color: result.ok ? '#137333' : '#c5221f',
              border: `1px solid ${result.ok ? '#81c995' : '#f5c6c6'}`,
            }}>
              {result.msg}
            </div>
          )}

          <div style={{ marginTop: 16, fontSize: 11, color: '#9aa0a6', lineHeight: 1.5 }}>
            The QR code is HMAC-SHA256 signed — forged or tampered codes are automatically rejected.
          </div>
        </div>
      </div>
    </div>
  );
}
